import React from 'react';
import { History, TrendingDown, CalendarX, ArrowRight } from 'lucide-react';

const pctColor = (pct, hours) => {
  if (!hours) return 'bg-white/[0.04] text-white/25';
  if (pct > 100) return 'bg-[#B71C5C]/70 text-white';
  if (pct >= 90) return 'bg-[#FFD66B]/80 text-[#1A1325]';
  if (pct >= 60) return 'bg-[#34C98E]/60 text-white';
  return 'bg-[#3E6FD9]/40 text-white/80';
};

export const TrendCards = ({ trends }) => {
  if (!trends?.length) return null;
  return (
    <div className="space-y-1.5" data-testid="trend-cards">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/40">
        <History className="h-3.5 w-3.5" /> History intelligence — past 6 months
      </div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {trends.map((t, i) => (
          <div key={i} className="flex items-start gap-2 bg-[#221A33]/60 border border-[#6B3FA0]/30 rounded-xl px-3 py-2 text-xs text-white/70">
            {t.kind === 'underworked' ? <TrendingDown className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#7EA0FF]" /> : <CalendarX className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#FF7BA9]" />}
            {t.detail}
          </div>
        ))}
      </div>
    </div>
  );
};

export const MonthBoard = ({ data, openWeek }) => {
  const weekLbl = (w) => new Date(w + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' });
  return (
    <div className="space-y-4" data-testid="month-board">
      <TrendCards trends={data.trends} />

      {/* week summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {data.week_summaries.map(w => (
          <button key={w.week_start} onClick={() => openWeek(w.week_start)}
                  className="text-left bg-white/[0.04] border border-white/10 hover:border-[#FFD66B]/60 rounded-xl p-3 group transition-colors"
                  data-testid={`month-week-${w.week_start}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 flex items-center justify-between">
              Wk of {weekLbl(w.week_start)} <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 text-[#FFD66B]" />
            </div>
            <div className="text-lg font-bold">{w.total_hours}h</div>
            <div className="flex gap-2 text-[10px] mt-0.5">
              <span className={w.gap_count ? 'text-[#FF7BA9] font-bold' : 'text-white/30'}>{w.gap_count} gaps</span>
              <span className={w.conflict_count ? 'text-[#FFD66B] font-bold' : 'text-white/30'}>{w.conflict_count} conf</span>
              <span className="text-white/30">{w.avg_utilization}%</span>
            </div>
          </button>
        ))}
      </div>

      {/* per-staff heat strip */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 overflow-x-auto">
        <div className="text-sm font-bold mb-3">Staff month heat map — hours vs weekly target</div>
        <table className="w-full text-xs" data-testid="month-heatmap">
          <thead>
            <tr className="text-white/40 text-[10px] uppercase tracking-wider">
              <th className="text-left pb-2 pr-3 font-bold">Staff</th>
              {data.week_starts.map(w => <th key={w} className="pb-2 px-1 font-bold">{weekLbl(w)}</th>)}
              <th className="pb-2 pl-3 text-right font-bold">Month</th>
              <th className="pb-2 pl-2 text-right font-bold">6-mo avg</th>
            </tr>
          </thead>
          <tbody>
            {data.staff.map(s => (
              <tr key={s.id} className="border-t border-white/5" data-testid={`month-row-${s.id}`}>
                <td className="py-1.5 pr-3 font-semibold whitespace-nowrap">{s.name}</td>
                {s.weeks.map(w => (
                  <td key={w.week_start} className="px-1 py-1.5">
                    <button onClick={() => openWeek(w.week_start)} title={`${w.hours}h (${w.pct}%) — open week`}
                            className={`w-full min-w-[52px] rounded-md py-1 text-center font-bold ${pctColor(w.pct, w.hours)} hover:ring-1 hover:ring-[#FFD66B]`}>
                      {w.hours ? `${w.hours}h` : '—'}
                    </button>
                  </td>
                ))}
                <td className="pl-3 py-1.5 text-right whitespace-nowrap">
                  <span className="font-bold">{s.month_hours}h</span>
                  <span className="text-white/30"> / {s.month_capacity}h</span>
                  <span className={`ml-1.5 font-bold ${s.month_pct > 100 ? 'text-[#FF7BA9]' : s.month_pct < 60 ? 'text-[#7EA0FF]' : 'text-[#34C98E]'}`}>{s.month_pct}%</span>
                </td>
                <td className="pl-2 py-1.5 text-right text-white/50 whitespace-nowrap">{s.hist_avg_weekly != null ? `${s.hist_avg_weekly}h/wk` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex gap-3 mt-3 text-[10px] text-white/40">
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#3E6FD9]/40 mr-1" />Under 60%</span>
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#34C98E]/60 mr-1" />60–89%</span>
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#FFD66B]/80 mr-1" />90–100%</span>
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-[#B71C5C]/70 mr-1" />Over</span>
        </div>
      </div>
      <p className="text-xs text-white/30">Tap any week cell or week card to jump into that week for Smart Fill, AI modeling, and edits.</p>
    </div>
  );
};

export default MonthBoard;
